import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Box,
  Button,
  Card,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import CountdownTimer, {
  defaultGetFormattedDate,
} from '../../../components/CountdownTimer'
import { CountryFlagCircle } from '../../countryFilter/views/CountryFlagSelect'
import COLORS from '../../../styles/colors'
import { MonitoredReading } from '../hooks/useLiveCallMonitorData'
import {
  ParticipantHealthIcon,
  ReadingHealthBadge,
} from './LiveCallMonitorHealth'
import LiveCallMonitorSection from './LiveCallMonitorSection'

export type LiveCallMonitorItemProps = {
  reading: MonitoredReading
}

const ACTUALLY_IN_PROGRESS_STATES = ['In Progress', 'Ending']

const STATUS_COLORS: Record<string, string> = {
  Scheduled: '#E0E0E0',
  Starting: '#FFF6B8',
  'In Progress': '#CDEFC4',
  Ending: '#FFD9B3',
}

const orangeButtonSx = {
  color: COLORS.burnedOrange,
  borderColor: COLORS.burnedOrange,
  '&:hover': {
    backgroundColor: COLORS.darkerOrange,
    borderColor: COLORS.darkerOrange,
    color: COLORS.white,
  },
}

const formatTime = (date: Date) =>
  date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

const capitalize = (value: string) =>
  value.length > 0 ? value.charAt(0).toUpperCase() + value.slice(1) : value

// `readingevent.created` is a Postgres `timestamp` (no offset) stored as UTC, unlike
// `readingstart` (`timestamptz`) - without a "Z" it would be parsed as browser-local time.
const HAS_TIMEZONE = /Z$|[+-]\d{2}:?\d{2}$/
const parseUtcTimestamp = (value: string) =>
  new Date(HAS_TIMEZONE.test(value) ? value : `${value}Z`)

const LiveCallMonitorItem = ({ reading }: LiveCallMonitorItemProps) => {
  const { t } = useTranslation(['LiveCallMonitor', 'ReadingStatus'])
  const router = useRouter()

  const statusName = reading.readingEventStatusName || ''
  const isActuallyInProgress = ACTUALLY_IN_PROGRESS_STATES.includes(statusName)
  const bookName = reading.book?.user?.person?.name || ''

  const scheduledStart = new Date(reading.readingstart)
  // Time left counts from the scheduled start, so a late opening shortens the reading.
  const scheduledEnd = new Date(
    scheduledStart.getTime() + (reading.duration?.minutes ?? 0) * 60000,
  )
  const openedAt = reading.readingevents[0]?.created
    ? parseUtcTimestamp(reading.readingevents[0].created)
    : scheduledStart
  const openedOffsetMinutes = Math.floor(
    (openedAt.getTime() - scheduledStart.getTime()) / 60000,
  )
  const openedOffsetLabel = `${openedOffsetMinutes >= 0 ? '+' : ''}${openedOffsetMinutes}`

  // TODO(phase 2): swap for the real `needs_help` column once it exists on live_call_state (see /memories/session/plan.md)
  const needsHelp = reading.liveParticipants.some(
    (participant) =>
      participant.role === 'book' &&
      (participant as { needs_help?: boolean }).needs_help === true,
  )

  const issueCount = reading.liveParticipants.filter(
    ({ health }) => health.level === 'warning' || health.level === 'critical',
  ).length

  return (
    <Card sx={{ p: '16px', backgroundColor: COLORS.white }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Stack spacing="4px">
          <Typography variant="inter_h4" fontWeight={600}>
            {bookName || t('unknownBook')}
          </Typography>
          <Typography variant="inter_bodyText" color={COLORS.grey}>
            {reading.topicName || ''}
          </Typography>
        </Stack>
        <Stack direction="row" spacing="8px" alignItems="center">
          {reading.country?.isocode && (
            <CountryFlagCircle isocode={reading.country.isocode} />
          )}
          {needsHelp && (
            <Tooltip title={t('needsHelp')}>
              <FontAwesomeIcon
                color={COLORS.red}
                icon={faTriangleExclamation}
              />
            </Tooltip>
          )}
          <ReadingHealthBadge participants={reading.liveParticipants} />
          <Chip
            label={t(statusName, { ns: 'ReadingStatus' })}
            size="small"
            sx={{
              backgroundColor:
                STATUS_COLORS[statusName] || STATUS_COLORS.Scheduled,
              color: COLORS.greyDark,
              cursor: 'default',
              pointerEvents: 'none',
            }}
          />
        </Stack>
      </Stack>

      <Stack mt="12px" spacing="2px">
        <Typography variant="inter_bodyText" color={COLORS.grey}>
          {t('scheduledFor', { time: formatTime(scheduledStart) })}
        </Typography>
        {isActuallyInProgress && (
          <>
            <Typography variant="inter_bodyText" color={COLORS.grey}>
              {t('startedAt', {
                time: formatTime(openedAt),
                offset: openedOffsetLabel,
              })}
            </Typography>
            <Typography variant="inter_bodyText" color={COLORS.grey}>
              {t('timeLeft')}{' '}
              <CountdownTimer
                date={scheduledEnd}
                getFormattedDate={(timeRemaining, units) =>
                  timeRemaining.total <= 0
                    ? `0 ${units.minutes}`
                    : (defaultGetFormattedDate?.(timeRemaining, units) ?? '')
                }
              />
            </Typography>
          </>
        )}
        {statusName === 'Scheduled' && (
          <Typography variant="inter_bodyText" color={COLORS.grey}>
            {t('startsIn')} <CountdownTimer date={scheduledStart} />
          </Typography>
        )}
      </Stack>

      <LiveCallMonitorSection
        title={t('signedUp.title')}
        description={t('signedUp.description', {
          count: reading.signedUpReaders.length,
        })}
        toggleLabel={t('signedUp.toggle')}
      >
        {reading.signedUpReaders.length === 0 && (
          <Typography variant="inter_bodyText" color={COLORS.grey}>
            {t('signedUp.empty')}
          </Typography>
        )}
        {reading.signedUpReaders.map((reader) => (
          <Stack
            key={reader.userId}
            onClick={() => router.push(`/admin/user/${reader.userId}`)}
            sx={{
              cursor: 'pointer',
              borderRadius: '4px',
              '&:hover': { backgroundColor: COLORS.backgroundGreyLight },
            }}
          >
            <Typography
              variant="inter_bodyText"
              sx={{ fontSize: '13px', overflowWrap: 'anywhere' }}
            >
              {reader.name}
            </Typography>
          </Stack>
        ))}
      </LiveCallMonitorSection>

      <LiveCallMonitorSection
        title={t('online.title')}
        description={[
          t('online.description', { count: reading.liveParticipants.length }),
          issueCount > 0 ? t('online.issues', { count: issueCount }) : '',
        ]
          .filter(Boolean)
          .join(' · ')}
        toggleLabel={t('online.toggle')}
      >
        {reading.liveParticipants.length === 0 && (
          <Typography variant="inter_bodyText" color={COLORS.greyDark}>
            {t('noLiveParticipants')}
          </Typography>
        )}
        {reading.liveParticipants.map((participant) => {
          const isPresent = participant.isPresent

          return (
            <Box
              key={participant.user_id}
              onClick={() => router.push(`/admin/user/${participant.user_id}`)}
              sx={{
                alignItems: 'center',
                borderRadius: '4px',
                columnGap: '12px',
                cursor: 'pointer',
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) 64px 92px 24px',
                '&:hover': { backgroundColor: COLORS.backgroundGreyLight },
              }}
            >
              <Typography
                variant="inter_bodyText"
                sx={{ fontSize: '13px', overflowWrap: 'anywhere' }}
              >
                {participant.personName}
              </Typography>
              <Typography
                variant="inter_bodyText"
                color={COLORS.grey}
                sx={{ fontSize: '13px' }}
              >
                {participant.role ? capitalize(participant.role) : ''}
              </Typography>
              <Tooltip
                title={
                  isPresent
                    ? t('online.presentExplanation')
                    : t('online.notPresentExplanation')
                }
                placement="top"
                arrow
              >
                <Typography
                  variant="inter_bodyText"
                  color={isPresent ? COLORS.green : COLORS.red}
                  sx={{ cursor: 'help', fontSize: '13px' }}
                >
                  {isPresent ? t('present') : t('notPresent')}
                </Typography>
              </Tooltip>
              <ParticipantHealthIcon health={participant.health} />
            </Box>
          )
        })}
      </LiveCallMonitorSection>

      <Stack direction="row" spacing="8px" mt="16px">
        <Button
          fullWidth
          variant="outlined"
          sx={orangeButtonSx}
          onClick={() => router.push(`/reading-room/${reading.id}`)}
        >
          {t('goToReadingRoom')}
        </Button>
        <Button
          fullWidth
          variant="outlined"
          sx={orangeButtonSx}
          onClick={() => router.push(`/admin/readings/${reading.id}`)}
        >
          {t('goToAdminPage')}
        </Button>
      </Stack>
    </Card>
  )
}

export default LiveCallMonitorItem
